/**
 * Camera frame / state / controller interfaces.
 *
 * Simplified from supersplat-viewer's Camera/CameraFrame: we drop orbit-style
 * focus point / distance (first-person only) and implement our own InputFrame
 * so we don't depend on pc.InputFrame internals.
 */

import { Vec3 } from 'playcanvas';

interface FrameData {
    /** [strafe, verticalOrJump, forward] — see InputController semantics. */
    move: number[];
    /** [yawDelta, pitchDelta, rollDelta] in degrees. */
    rotate: number[];
    /** True when the input source intends to run this frame. */
    running: boolean;
}

/** Accumulates input deltas; `read()` returns and clears them. */
class CameraFrame {
    move: number[] = [0, 0, 0];
    rotate: number[] = [0, 0, 0];
    running = false;

    appendMove(x: number, y: number, z: number) {
        this.move[0] += x;
        this.move[1] += y;
        this.move[2] += z;
    }

    appendRotate(x: number, y: number, z: number) {
        this.rotate[0] += x;
        this.rotate[1] += y;
        this.rotate[2] += z;
    }

    setRunning(running: boolean) {
        this.running = this.running || running;
    }

    read(): FrameData {
        const data: FrameData = {
            move: [this.move[0], this.move[1], this.move[2]],
            rotate: [this.rotate[0], this.rotate[1], this.rotate[2]],
            running: this.running
        };
        this.move[0] = this.move[1] = this.move[2] = 0;
        this.rotate[0] = this.rotate[1] = this.rotate[2] = 0;
        this.running = false;
        return data;
    }
}

/** Mutable camera pose written by controllers, read by PlayerRig. */
class CameraState {
    position = new Vec3();
    /** [pitch, yaw, roll] in degrees. */
    angles = new Vec3();
    fov = 75;
    /**
     * Camera local position offset from `position` (in world-oriented space,
     * since PlayerRoot carries no rotation). First-person controllers leave
     * this at (0,0,0); the third-person controller writes the orbit offset
     * (behind/above the player) each frame. Read by PlayerRig.apply.
     */
    cameraOffset = new Vec3();

    copy(s: CameraState) {
        this.position.copy(s.position);
        this.angles.copy(s.angles);
        this.fov = s.fov;
        this.cameraOffset.copy(s.cameraOffset);
    }
}

interface CameraController {
    onEnter(state: CameraState): void;
    update(deltaTime: number, inputFrame: CameraFrame, state: CameraState): void;
    onExit(state: CameraState): void;
    resetToSpawn?(state: CameraState): boolean;
}

export { CameraFrame, CameraState };
export type { CameraController, FrameData };
